import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { AuthSplitShell } from '@/components/auth/AuthSplitShell'
import { LoginForm } from '@/components/auth/LoginForm'
import { DemoAccountsPanel } from '@/components/auth/DemoAccountsPanel'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { login } from '@/store/authSlice'
import type { LoginFormData } from '@/lib/validations/auth'

interface LocationState {
  from?: { pathname?: string }
}

export default function LoginPage() {
  const { t } = useTranslation('auth')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated } = useAppSelector(s => s.auth)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  // Used to remount the form so demo auto-fill is visible in the fields.
  const [prefill, setPrefill] = useState<Partial<LoginFormData> | undefined>()
  const [formKey, setFormKey] = useState(0)

  // AC-2.1.1-023: valid session -> go straight to Dashboard.
  if (isAuthenticated) {
    const dest =
      (location.state as LocationState)?.from?.pathname ?? '/dashboard'
    return <Navigate to={dest} replace />
  }

  const submitLogin = async (data: LoginFormData) => {
    setSubmitting(true)
    setFormError(null)
    try {
      await dispatch(login(data)).unwrap()
      const dest =
        (location.state as LocationState)?.from?.pathname ?? '/dashboard'
      navigate(dest, { replace: true })
    } catch (message) {
      // Thunk rejects with a human-readable string (generic / network message).
      setFormError(
        typeof message === 'string' ? message : t('login.invalidCredentials')
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleDemoSelect = (creds: LoginFormData) => {
    setPrefill(creds)
    setFormKey(k => k + 1)
    void submitLogin(creds)
  }

  return (
    <AuthSplitShell
      footer={
        <DemoAccountsPanel onSelect={handleDemoSelect} disabled={submitting} />
      }
    >
      <div className='space-y-8'>
        <h1 className='font-heading text-[24px] font-semibold leading-[1.2] tracking-[-0.02em] text-[#171A20]'>
          {t('login.title')}
        </h1>

        <LoginForm
          key={formKey}
          onSubmit={submitLogin}
          submitting={submitting}
          formError={formError}
          onDismissFormError={() => setFormError(null)}
          defaultValues={prefill}
        />
      </div>
    </AuthSplitShell>
  )
}
